import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock, PlayCircle, RefreshCw, Sparkles, Undo2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, ModuleOff, PageHeader, Reveal } from "@/components/common";
import { DayStrip } from "@/components/DayStrip";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { ActivityList } from "@/pages/Activities";
import { Link } from "react-router-dom";
import { AiPlanButton, useDeviationAdapt } from "@/components/AiPlanDialog";
import { todayIso, mergePlanDays } from "@/lib/dates";

const ExerciseRow = ({ item, i }) => (
  <Reveal delay={i * 0.04} className="flex gap-4 rounded-2xl bg-white p-4">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-train-soft font-display font-extrabold text-train-ink">{i + 1}</span>
    <div className="min-w-0 flex-1" data-testid={`exercise-${i}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-ink">{item.exercise}</p>
        <p className="font-display text-sm font-bold text-train-ink">{item.series} × {item.reps}</p>
      </div>
      {item.activity_title && <p className="mt-0.5 text-xs font-medium text-train-ink">{item.activity_title}</p>}
      <p className="mt-1 text-sm text-stone-600">{item.care}</p>
      {item.substituted_from && <p className="mt-2 inline-block rounded-full bg-care-soft px-3 py-1 text-xs font-semibold text-care-ink" data-testid={`exercise-substituted-${i}`}>Sustituye a: {item.substituted_from}</p>}
      <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(item.exercise + " técnica ejercicio")}`} target="_blank" rel="noreferrer" data-testid={`exercise-video-${i}`} className="mt-2 flex w-fit items-center gap-1 text-xs font-semibold text-stone-500 hover:text-train-ink">
        <PlayCircle className="h-3.5 w-3.5" /> Ver técnica
      </a>
    </div>
  </Reveal>
);

const ActivitiesBlock = ({ activities }) => (
  <div className="mt-4 space-y-2" data-testid="workout-activities-list">
    <p className="text-xs font-bold uppercase tracking-wide text-train-ink">Lo que registraste</p>
    {activities.map((a, i) => (
      <div key={i} className="rounded-2xl bg-white px-4 py-3" data-testid={`workout-activity-${i}`}>
        <p className="font-semibold text-ink">{a.title}</p>
        <p className="mt-0.5 text-sm text-stone-600">
          {a.sport} · {a.minutes} min{a.distance_km != null ? ` · ${a.distance_km} km` : ""}
        </p>
        {(a.exercises || []).length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-stone-600">
            {a.exercises.map((ex, j) => (
              <li key={j}>· {ex.exercise}{ex.series ? ` — ${ex.series}×${ex.reps || "—"}` : ""}</li>
            ))}
          </ul>
        )}
      </div>
    ))}
  </div>
);

const FeedbackSummary = ({ fb, onUndo, onEdit, alternative }) => (
  <div className="rounded-2xl bg-food-soft p-4" data-testid="workout-completed-summary">
    <p className="flex items-center gap-2 font-semibold text-food-ink"><CheckCircle2 className="h-5 w-5" /> Sesión completada</p>
    {alternative && <p className="mt-1 text-sm font-medium text-train-ink" data-testid="workout-did-alternative">Hiciste: {alternative}</p>}
    <p className="mt-1 text-sm text-stone-700">Dolor {fb.pain}/10 · Esfuerzo {fb.rpe}/10 · Valoración {fb.rating}/5</p>
    {fb.notes && <p className="mt-1 text-sm italic text-stone-600">“{fb.notes}”</p>}
    <div className="mt-3 flex flex-wrap gap-3">
      <button type="button" onClick={onEdit} data-testid="workout-edit-btn" className="text-xs font-semibold text-train-ink hover:underline">Cambiar registro</button>
      <button type="button" onClick={onUndo} data-testid="workout-undo-btn" className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-ink"><Undo2 className="h-3.5 w-3.5" /> Deshacer</button>
    </div>
  </div>
);

const SkippedSummary = ({ fb }) => (
  <div className="rounded-2xl bg-stone-100 p-4" data-testid="workout-skipped-summary">
    <p className="flex items-center gap-2 font-semibold text-stone-700"><XCircle className="h-5 w-5" /> Sesión marcada como no realizada</p>
    <p className="mt-1 text-sm text-stone-600">
      {fb.source === "auto"
        ? "Se cerró al pasar el día. Si entrenaste (o hiciste otra cosa), puedes corregirlo abajo."
        : "La marcaste como omitida. Puedes corregirlo abajo."}
    </p>
    {fb.notes && <p className="mt-1 text-sm italic text-stone-600">“{fb.notes}”</p>}
  </div>
);

export default function Training() {
  const { me } = useAuth();
  const [plan, setPlan] = useState(null);
  const [selDate, setSelDate] = useState(null);
  const [open, setOpen] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState("planned");
  const [busy, setBusy] = useState(false);

  const openFeedback = (mode) => {
    setFeedbackMode(mode);
    setOpen(true);
  };
  const [sports, setSports] = useState([]);
  const [preferredSports, setPreferredSports] = useState([]);
  const on = me.modules?.training;
  const load = () => api.get("/plan").then((r) => setPlan(r.data));
  const { trigger: triggerAdapt, dialog: adaptDialog } = useDeviationAdapt(load);
  useEffect(() => {
    if (!on) return;
    load();
    api.get("/plan/catalog").then((r) => {
      setSports(r.data.sports || []);
      setPreferredSports(r.data.preferred_sports || []);
    }).catch(() => {});
  }, [on]);

  const days = useMemo(() => (plan ? mergePlanDays(plan) : []), [plan]);
  useEffect(() => {
    if (!days.length) return;
    const t = todayIso();
    setSelDate((prev) => {
      if (prev && days.some((d) => d.date === prev)) return prev;
      return days.some((d) => d.date === t) ? t : days[0].date;
    });
  }, [days]);

  const sel = selDate ? days.findIndex((d) => d.date === selDate) : -1;

  if (!on) return <ModuleOff module="training" />;
  if (!plan || sel < 0 || !days[sel]) return <Loader />;
  const day = days[sel];
  const w = day.workout;
  const future = day.date > todayIso();
  const done = days.filter((d) => d.feedback?.completed).length;
  const altLabel = w?.activities?.length
    ? w.activities.map((a) => a.title).join(" · ")
    : (w?.did_alternative ? w.title : null);

  const patchDay = (updated) => {
    setPlan((p) => {
      if (!p) return p;
      const inDays = p.days.some((d) => d.date === updated.date);
      if (inDays) return { ...p, days: p.days.map((d) => (d.date === updated.date ? updated : d)) };
      return { ...p, history: (p.history || []).map((d) => (d.date === updated.date ? updated : d)) };
    });
  };

  const complete = async (fb) => {
    setBusy(true);
    try {
      const plannedTitle = w?.title;
      const { data } = await api.post(`/plan/days/${day.date}/complete`, fb);
      const isAlt = feedbackMode === "actual" || !!(fb.actual_activities?.length);
      toast.success(
        data.adjusted_days
          ? `¡Sesión guardada! Hemos ajustado ${data.adjusted_days} día(s) siguientes.`
          : "¡Sesión guardada! Sigue así.",
      );
      setOpen(false);
      patchDay(data.day);
      load();
      if (isAlt) {
        const actual = (fb.actual_activities || []).map((a) => a.title).filter(Boolean).join(" · ") || "otra actividad";
        triggerAdapt({ kind: "workout_alt", date: day.date, planned: plannedTitle, actual });
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const undo = async () => {
    const { data } = await api.post(`/plan/days/${day.date}/uncomplete`);
    patchDay(data.day);
    load();
  };
  const editCompleted = async (mode = "planned") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plan/days/${day.date}/uncomplete`);
      patchDay(data.day);
      openFeedback(mode);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const skip = async () => {
    setBusy(true);
    try {
      const plannedTitle = w?.title;
      const { data } = await api.post(`/plan/days/${day.date}/skip`, {});
      toast.success(
        data.adjusted_days
          ? `Registrado como no hecho. Ajustados ${data.adjusted_days} día(s) siguientes.`
          : "Registrado como no realizado.",
      );
      patchDay(data.day);
      load();
      triggerAdapt({ kind: "workout_skip", date: day.date, planned: plannedTitle });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const clearSkip = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plan/days/${day.date}/uncomplete`);
      patchDay(data.day);
      toast.success("Omisión quitada");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const regenerate = async () => { const { data } = await api.post("/plan/regenerate"); setPlan(data); toast.success("Plan recalculado con tu perfil actual"); };

  return (
    <div data-testid="training-page">
      <PageHeader eyebrow="Entrenador" tone="train" title="Tu plan de entreno" subtitle={`Incluye días pasados recientes · ${done} sesiones completadas`}>
        <button onClick={regenerate} data-testid="training-regenerate-btn" className="mt-4 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold shadow-sm transition-transform active:scale-95"><RefreshCw className="h-3.5 w-3.5" /> Recalcular plan</button>
        <div className="mt-2 flex flex-wrap gap-2"><AiPlanButton onApplied={load} testid="training-ai-plan-btn" />
        <Link to="/agenda" data-testid="training-agenda-link" className="inline-flex rounded-full bg-white px-4 py-2 text-xs font-semibold shadow-sm">Ver agenda</Link>
        <Link to="/actividad" data-testid="training-import-link" className="inline-flex rounded-full bg-train px-4 py-2 text-xs font-semibold text-white">Importar actividad (Strava, Xiaomi…)</Link></div>
      </PageHeader>
      <DayStrip
        days={days}
        selected={sel}
        onSelect={(i) => days[i] && setSelDate(days[i].date)}
        tone="train"
        isDone={(d) => d.feedback?.completed}
        isSkipped={(d) => d.feedback?.skipped && !d.feedback?.completed}
      />
      <div className="mt-6 rounded-3xl bg-train-soft p-5 sm:p-7" key={day.date}>
        <p className="eyebrow text-train-ink">{day.label}{w?.kind === "descanso" ? " · Día suave" : ""}{day.date < todayIso() ? " · Pasado" : ""}</p>
        <h2 className="mt-1 text-2xl font-extrabold" data-testid="workout-title">{w?.title}</h2>
        {w?.did_alternative && w.replaced_from_title && (
          <p className="mt-1 text-sm font-medium text-train-ink" data-testid="workout-replaced-banner">En vez de: {w.replaced_from_title}</p>
        )}
        <p className="mt-1 flex items-center gap-1.5 text-sm text-stone-600">
          <Clock className="h-4 w-4" /> {w?.minutes} min
          {w?.notes ? ` · ${w.notes}` : ""}
        </p>
        {w?.activities?.length > 0 && <ActivitiesBlock activities={w.activities} />}
        {(day.replan_notes || []).length > 0 && (
          <div className="mt-4 space-y-1 rounded-2xl bg-white p-4 text-sm" data-testid="workout-replan-notes">
            {day.replan_notes.map((n, i) => <p key={i} className="flex gap-2"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-train" />{n}</p>)}
          </div>
        )}
        {!(w?.activities?.length) && (
          <div className="mt-5 space-y-3">{(w?.items || []).map((it, i) => <ExerciseRow key={i} item={it} i={i} />)}</div>
        )}
        {w?.activities?.length > 0 && (w.items || []).some((it) => it.exercise && !it.activity_title) && (
          <div className="mt-5 space-y-3">{(w.items || []).map((it, i) => <ExerciseRow key={i} item={it} i={i} />)}</div>
        )}
        <div className="mt-5"><ActivityList day={day.date} compact /></div>
        <div className="mt-6 space-y-2">
          {future ? (
            <button disabled className="w-full rounded-full bg-stone-300 py-3.5 font-semibold text-white" data-testid="workout-complete-btn">
              Disponible ese día
            </button>
          ) : day.feedback?.completed ? (
            <FeedbackSummary
              fb={day.feedback}
              onUndo={undo}
              onEdit={() => editCompleted(day.feedback?.did_as_planned === false ? "actual" : "planned")}
              alternative={altLabel}
            />
          ) : (
            <>
              {day.feedback?.skipped && <SkippedSummary fb={day.feedback} />}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={!w || busy}
                  onClick={() => openFeedback("planned")}
                  data-testid="workout-complete-btn"
                  className="w-full rounded-full bg-train py-3.5 font-semibold text-white transition-transform active:scale-95 disabled:bg-stone-300"
                >
                  {day.feedback?.skipped ? "Sí entrené (como estaba planeado)" : "Marcar como hecho"}
                </button>
                <button
                  type="button"
                  disabled={!w || busy || w?.kind === "descanso"}
                  onClick={() => openFeedback("actual")}
                  data-testid="workout-alternative-btn"
                  className="w-full rounded-full border-2 border-train bg-white py-3.5 font-semibold text-train-ink transition-transform active:scale-95 disabled:border-stone-200 disabled:text-stone-400"
                >
                  Hice otra cosa
                </button>
              </div>
              {w && w.kind !== "descanso" && !day.feedback?.skipped && (
                <button disabled={busy} onClick={skip} data-testid="workout-skip-btn" className="w-full rounded-full py-2.5 text-sm font-semibold text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-700 disabled:opacity-60">
                  No entrené nada
                </button>
              )}
              {day.feedback?.skipped && (
                <button disabled={busy} onClick={clearSkip} data-testid="workout-unskip-btn" className="flex w-full items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold text-stone-500 hover:bg-stone-100 hover:text-stone-700 disabled:opacity-60">
                  <Undo2 className="h-3.5 w-3.5" /> Quitar omisión
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <FeedbackDialog
        open={open}
        onOpenChange={setOpen}
        onSubmit={complete}
        busy={busy}
        plannedTitle={w?.title}
        plannedMinutes={w?.minutes}
        sports={sports}
        preferredSports={preferredSports}
        initialMode={feedbackMode}
      />
      {adaptDialog}
    </div>
  );
}
